import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  BookOpen, Plus, Edit, Trash2, Package, DollarSign, 
  ShoppingCart, TrendingUp, RefreshCw, Clock, Download, PlusCircle, MinusCircle
} from "lucide-react";
import { Switch } from "../../components/ui/switch";

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api/libritos';

export default function AdminLibritosPage() {
  const [books, setBooks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookModal, setShowBookModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [stockBook, setStockBook] = useState(null);
  const [stockChange, setStockChange] = useState({ quantity: 0, reason: "ingreso" });
  const [editingBook, setEditingBook] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    stock: "",
    image_url: "",
    is_free: false,
    download_url: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [booksRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/books`),
        axios.get(`${API_URL}/dashboard/stats`)
      ]);
      setBooks(booksRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingBook(null);
    setFormData({ title: "", description: "", price: "", stock: "", image_url: "", is_free: false, download_url: "" });
    setShowBookModal(true);
  };

  const openEditModal = (book) => {
    setEditingBook(book);
    setFormData({
      title: book.title,
      description: book.description || "",
      price: book.price?.toString() || "0",
      stock: book.stock?.toString() || "0",
      image_url: book.image_url || "",
      is_free: book.is_free || false,
      download_url: book.download_url || ""
    });
    setShowBookModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        title: formData.title,
        description: formData.description || null,
        price: formData.is_free ? 0 : parseFloat(formData.price || 0),
        stock: formData.is_free ? 0 : parseInt(formData.stock || 0),
        image_url: formData.image_url || null,
        is_free: formData.is_free,
        download_url: formData.is_free ? (formData.download_url || null) : null
      };

      if (editingBook) {
        await axios.put(`${API_URL}/books/${editingBook.id}`, data);
        toast.success("Libro actualizado");
      } else {
        await axios.post(`${API_URL}/books`, data);
        toast.success("Libro creado");
      }
      setShowBookModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar libro");
    }
  };

  const handleDelete = async (bookId) => {
    if (!window.confirm("¿Eliminar este libro?")) return;
    try {
      await axios.delete(`${API_URL}/books/${bookId}`);
      toast.success("Libro eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar libro");
    }
  };

  const openStockModal = (book) => {
    setStockBook(book);
    setStockChange({ quantity: 0, reason: "ingreso" });
    setShowStockModal(true);
  };

  const handleStockAdjust = async () => {
    if (stockChange.quantity === 0) {
      toast.error("Ingresá una cantidad");
      return;
    }
    try {
      await axios.post(`${API_URL}/books/${stockBook.id}/adjust-stock`, {
        quantity_change: stockChange.quantity,
        reason: stockChange.reason
      });
      toast.success(`Stock actualizado: ${stockChange.quantity > 0 ? '+' : ''}${stockChange.quantity}`);
      setShowStockModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al ajustar stock");
    }
  };

  const checkExpired = async () => {
    try {
      const res = await axios.post(`${API_URL}/reservations/expire-check`);
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error("Error al verificar reservas");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Libritos</h1>
          <p className="text-gray-500">Gestión de libros y reservas</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={checkExpired}>
            <Clock className="w-4 h-4 mr-2" />
            Verificar Vencidas
          </Button>
          <Button onClick={openCreateModal} className="bg-violet-600 hover:bg-violet-700">
            <Plus className="w-4 h-4 mr-2" />
            Nuevo Libro
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Libros</p>
                  <p className="text-3xl font-bold text-violet-600">{stats.total_books}</p>
                  <p className="text-xs text-gray-400">Stock total: {stats.total_stock}</p>
                </div>
                <BookOpen className="w-10 h-10 text-violet-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Reservas Activas</p>
                  <p className="text-3xl font-bold text-amber-600">{stats.active_reservations}</p>
                  <p className="text-xs text-gray-400">Total: {stats.total_reservations}</p>
                </div>
                <ShoppingCart className="w-10 h-10 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Ventas Presenciales</p>
                  <p className="text-3xl font-bold text-blue-600">{stats.total_direct_sales}</p>
                  <p className="text-xs text-gray-400">${stats.revenue_direct_sales?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</p>
                </div>
                <DollarSign className="w-10 h-10 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Ingresos Reservas</p>
                  <p className="text-3xl font-bold text-purple-600">${stats.revenue_reservations?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}</p>
                  <p className="text-xs text-gray-400">Pagadas y entregadas</p>
                </div>
                <TrendingUp className="w-10 h-10 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Links */}
      <div className="flex gap-4 flex-wrap">
        <Link to="/admin/libritos/reservas">
          <Button variant="outline">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Ver Reservas
          </Button>
        </Link>
        <Link to="/admin/libritos/ventas">
          <Button variant="outline">
            <DollarSign className="w-4 h-4 mr-2" />
            Ventas Presenciales
          </Button>
        </Link>
        <Link to="/admin/libritos/movimientos">
          <Button variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Movimientos Stock
          </Button>
        </Link>
      </div>

      {/* Books Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Catálogo de Libros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Libro</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Precio</TableHead>
                <TableHead>Stock</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {books.map((book) => (
                <TableRow key={book.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {book.image_url ? (
                        <img src={book.image_url} alt={book.title} className="w-12 h-16 object-cover rounded" />
                      ) : (
                        <div className="w-12 h-16 bg-violet-100 rounded flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-violet-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{book.title}</p>
                        {book.description && (
                          <p className="text-sm text-gray-500 line-clamp-1">{book.description}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {book.is_free ? (
                      <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                        <Download className="w-3 h-3 mr-1" />
                        Gratuito
                      </Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100">De pago</Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-semibold">
                    {book.is_free ? "-" : `$${book.price?.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires' })}`}
                  </TableCell>
                  <TableCell>
                    {book.is_free ? (
                      <Badge className="bg-blue-100 text-blue-700">Online</Badge>
                    ) : book.stock === 0 ? (
                      <Badge className="bg-red-100 text-red-700">Sin stock</Badge>
                    ) : book.stock <= 3 ? (
                      <Badge className="bg-amber-100 text-amber-700">{book.stock}</Badge>
                    ) : (
                      <Badge className="bg-violet-100 text-violet-700">{book.stock}</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {!book.is_free && (
                      <Button variant="ghost" size="sm" onClick={() => openStockModal(book)} title="Ajustar stock">
                        <Package className="w-4 h-4 text-violet-600" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEditModal(book)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(book.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {books.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                    No hay libros registrados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Book Modal */}
      <Dialog open={showBookModal} onOpenChange={setShowBookModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingBook ? "Editar Libro" : "Nuevo Libro"}</DialogTitle>
            <DialogDescription>
              {editingBook ? "Modificá los datos del libro" : "Agregá un nuevo libro al catálogo"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title">Título *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
              />
            </div>
            
            {/* Toggle Libro Gratuito */}
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div>
                <Label htmlFor="is_free" className="font-medium">Libro gratuito / Descargable</Label>
                <p className="text-xs text-gray-500">Activar para libros online sin costo</p>
              </div>
              <Switch
                id="is_free"
                checked={formData.is_free}
                onCheckedChange={(checked) => setFormData({ ...formData, is_free: checked })}
              />
            </div>

            {formData.is_free ? (
              /* Campos para libro gratuito */
              <div>
                <Label htmlFor="download_url">URL de descarga (PDF) *</Label>
                <Input
                  id="download_url"
                  value={formData.download_url}
                  onChange={(e) => setFormData({ ...formData, download_url: e.target.value })}
                  placeholder="https://drive.google.com/..."
                  required
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Link directo al PDF o Google Drive</p>
              </div>
            ) : (
              /* Campos para libro de pago */
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="price">Precio *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="stock">Stock *</Label>
                  <Input
                    id="stock"
                    type="number"
                    value={formData.stock}
                    onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                    required
                    className="mt-1"
                  />
                </div>
              </div>
            )}
            
            <div>
              <Label htmlFor="image_url">URL de Imagen (portada)</Label>
              <Input
                id="image_url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowBookModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-violet-600 hover:bg-violet-700">
                {editingBook ? "Guardar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Modal */}
      <Dialog open={showStockModal} onOpenChange={setShowStockModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-violet-600" />
              Ajustar Stock
            </DialogTitle>
            <DialogDescription>
              {stockBook?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-violet-50 rounded-lg p-4 text-center">
              <p className="text-sm text-gray-500">Stock actual</p>
              <p className="text-3xl font-bold text-violet-700">{stockBook?.stock}</p>
            </div>

            <div>
              <Label>Motivo del ajuste</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setStockChange({ ...stockChange, reason: "ingreso" })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    stockChange.reason === "ingreso"
                      ? "border-green-500 bg-green-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <PlusCircle className={`h-5 w-5 mb-1 ${stockChange.reason === "ingreso" ? "text-green-600" : "text-gray-400"}`} />
                  <p className="font-medium text-sm">Ingreso</p>
                  <p className="text-xs text-gray-500">Compra de libros</p>
                </button>
                <button
                  type="button"
                  onClick={() => setStockChange({ ...stockChange, reason: "ajuste" })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    stockChange.reason === "ajuste"
                      ? "border-amber-500 bg-amber-50"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <RefreshCw className={`h-5 w-5 mb-1 ${stockChange.reason === "ajuste" ? "text-amber-600" : "text-gray-400"}`} />
                  <p className="font-medium text-sm">Ajuste</p>
                  <p className="text-xs text-gray-500">Corrección manual</p>
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="quantity">Cantidad a {stockChange.quantity >= 0 ? "agregar" : "quitar"}</Label>
              <div className="flex items-center gap-2 mt-2">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setStockChange({ ...stockChange, quantity: stockChange.quantity - 1 })}
                >
                  <MinusCircle className="w-4 h-4" />
                </Button>
                <Input
                  id="quantity"
                  type="number"
                  value={stockChange.quantity}
                  onChange={(e) => setStockChange({ ...stockChange, quantity: parseInt(e.target.value) || 0 })}
                  className="text-center text-xl font-bold"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setStockChange({ ...stockChange, quantity: stockChange.quantity + 1 })}
                >
                  <PlusCircle className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1 text-center">
                Usá números negativos para quitar stock
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-sm text-gray-500">Stock resultante</p>
              <p className={`text-2xl font-bold ${(stockBook?.stock || 0) + stockChange.quantity < 0 ? 'text-red-600' : 'text-violet-700'}`}>
                {(stockBook?.stock || 0) + stockChange.quantity}
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowStockModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleStockAdjust} 
                className="flex-1 bg-violet-600 hover:bg-violet-700"
                disabled={(stockBook?.stock || 0) + stockChange.quantity < 0}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
